import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { PublicHeader, Footer } from '@/components/layout/PublicHeader';

export default function LoginPage() {
  const { login, verifyMfa } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get('next') || '/menu';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  // Two-step 2FA state — populated after password success when the
  // server tells us the account has TOTP enabled.
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  const dispatchByRole = (u) => {
    if (['admin', 'owner'].includes(u.role)) nav('/server');
    else if (u.role === 'kitchen') nav('/kds');
    else if (u.role === 'cashier') nav('/pos');
    else if (u.role === 'waiter') nav('/waiter');
    else if (u.role === 'driver') nav('/driver');
    else nav(next);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      // login() returns either the user object OR {mfaRequired, mfaToken}
      if (result && result.mfaRequired) {
        setMfaToken(result.mfaToken);
        toast.message('Enter the 6-digit code from your authenticator app');
        return;
      }
      toast.success(`Welcome back, ${result.full_name}!`);
      dispatchByRole(result);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const onSubmitMfa = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(mfaCode)) { toast.error('Code must be 6 digits'); return; }
    setLoading(true);
    try {
      const u = await verifyMfa(mfaToken, mfaCode);
      toast.success(`Welcome back, ${u.full_name}!`);
      dispatchByRole(u);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (role) => {
    const creds = {
      customer: ['customer@citygrill.com', 'Customer@12345'],
      admin: ['admin@citygrill.com', 'Admin@12345'],
      kitchen: ['kitchen@citygrill.com', 'Kitchen@12345'],
      cashier: ['cashier@citygrill.com', 'Cashier@12345'],
      waiter: ['waiter@citygrill.com', 'Waiter@12345'],
      driver: ['driver@citygrill.com', 'Driver@12345'],
      owner: ['owner@citygrill.com', 'Owner@12345'],
    }[role];
    if (creds) { setEmail(creds[0]); setPassword(creds[1]); }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="mx-auto flex max-w-md flex-col gap-4 px-4 py-10 sm:px-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-2xl">Sign in</CardTitle>
            <CardDescription>Access your orders, table, or staff tools.</CardDescription>
          </CardHeader>
          <CardContent>
            {mfaToken ? (
              <form onSubmit={onSubmitMfa} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="mfa-code">6-digit code from your authenticator app</Label>
                  <Input
                    data-testid="login-mfa-code-input"
                    id="mfa-code"
                    inputMode="numeric"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoFocus
                    autoComplete="one-time-code"
                    placeholder="123456"
                    className="text-center font-mono text-xl tracking-widest"
                  />
                </div>
                <Button data-testid="login-mfa-verify-button" type="submit" className="w-full h-11" disabled={loading || mfaCode.length !== 6}>
                  {loading ? 'Verifying…' : 'Verify and sign in'}
                </Button>
                <button
                  type="button"
                  className="block w-full text-center text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => { setMfaToken(null); setMfaCode(''); }}
                  data-testid="login-mfa-cancel-button"
                >
                  Use a different account
                </button>
              </form>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input data-testid="login-email-input" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input data-testid="login-password-input" id="password" type={show ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
                  <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground" aria-label="toggle password visibility">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button data-testid="login-submit-button" type="submit" className="w-full h-11" disabled={loading}>{loading ? 'Signing in…' : 'Sign in'}</Button>
              <p className="text-center text-xs text-muted-foreground">
                <ShieldCheck className="mr-1 inline h-3.5 w-3.5" /> Secure session powered by JWT.
              </p>
            </form>
            )}
            <p className="mt-4 text-center text-sm text-muted-foreground">
              No account? <Link to="/signup" data-testid="login-signup-link" className="font-medium text-primary hover:underline">Create one</Link>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Demo accounts</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {['customer', 'admin', 'kitchen', 'cashier', 'waiter', 'driver', 'owner'].map((r) => (
                <Button key={r} data-testid={`login-demo-${r}-button`} size="sm" variant="secondary" type="button" onClick={() => quickFill(r)} className="capitalize">{r}</Button>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">Click a role to autofill credentials, then press Sign in.</p>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
